import axios, { AxiosError, AxiosHeaders } from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '@src/service/tokenManager';
import { config } from '@uhg-abyss/web/tools/config';
import { attachApiTelemetry } from './apiTelemetry';

const API_BASE_URL = config('API_BASE_URL');

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor: attach access token + refresh token headers
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    const refreshToken = getRefreshToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (refreshToken) {
      config.headers['X-Refresh-Token'] = refreshToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: read new tokens from response headers
api.interceptors.response.use(
  (response) => {
    const headers = response.headers;
    const newAccessToken = headers['x-new-access-token'];
    const newRefreshToken = headers['x-new-refresh-token'];

    if (newAccessToken) {
      const accessTokenStr = String(newAccessToken);
      const refreshTokenStr = newRefreshToken ? String(newRefreshToken) : undefined;
      sessionStorage.setItem('access_token', accessTokenStr);
      if (refreshTokenStr) {
        sessionStorage.setItem('refresh_token', refreshTokenStr);
      }
      console.log('Tokens refreshed successfully via backend filter');
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired and backend couldn't refresh — redirect to login
      clearTokens();
      window.location.href = '/auth/sso';
    }
    return Promise.reject(error);
  }
);

attachApiTelemetry(api);


// Helper function to ensure token is available
const ensureToken = async () => {
  if (!getAccessToken()) {
    throw new Error('Unable to retrieve auth token');
  }
};

// Helper to extract a meaningful error message from Axios errors
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    if (!axiosError.response) {
      // Network error or server is down
      return 'Unable to reach the server. Please check your connection or try again later.';
    }
    const status = axiosError.response.status;
    switch (status) {
      case 400: return 'Bad request. Please check the uploaded files.';
      case 401: return 'Unauthorized. Please log in again.';
      case 403: return 'Access denied.';
      case 404: return 'API endpoint not found.';
      case 500: return 'Internal server error. Please try again later.';
      default: return `Server responded with status ${status}.`;
    }
  }
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred.';
};


export const validateFiles = async (files: File[]): Promise<any> => {
  await ensureToken();
  try {
    const formData = new FormData();
    const requestId = crypto.randomUUID();
    formData.append('requestId', requestId);
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await api.post('/upload_validate', formData, {
      headers: { 'X-Request-Id': requestId },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.files) {
      return error.response.data;
    }
    const message = getErrorMessage(error);
    throw new Error(message);
  }
};

// API for processing validated files by request ID
export const processFilesByRequestId = async (requestId: string): Promise<any> => {
  await ensureToken();
  try {
    const formData = new FormData();
    formData.append('requestId', requestId);

    const response = await api.post(`/process`, formData, {
      headers: { 'X-Request-Id': requestId },
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    throw new Error(message);
  }
};

// API for processing uploaded files
export const processFiles = async (files: File[]): Promise<any> => {
  await ensureToken();
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

      const response = await api.post('/process', formData);
      if (response.status === 200) {
        return response.data;
      }
      throw new Error(`Server responded with status ${response.status}.`);
      // return fetch('/response_demo.json').then((res) => res.json());
  } catch (error) {
    const message = getErrorMessage(error);
    throw new Error(message);
  }
};

export const saveEditedInfo = async (
  requestId: string,
  section: 'triage' | 'summary',
  data: any
): Promise<any> => {
  await ensureToken();
  try {
    const editType = section === 'triage' ? 'triage_information' : 'descriptive_summary';
    const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(data))));
    // const encodedData = btoa(JSON.stringify(data));
    const formData = new FormData();
    formData.append('requestId', requestId);
    formData.append('editType', editType);
    formData.append('data', encodedData);

    const response = await api.put(`/saveEditInfo`, formData, {
      headers: { 'X-Request-Id': requestId },
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    throw new Error(message);
  }
};

